import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { CollapsibleSection } from "@/components/layout/collapsible-section";
import { Skeleton } from "@/components/ui/skeleton";
import type { CaseRef } from "@/features/case-detail/use-case-detail";

import type { DiagramMarks } from "./derive-diagram-marks";
import { useCaseDiagram } from "./use-case-diagram";
import { useModelDiagram } from "./use-model-diagram";

/** 図は開いたときだけ読み込む（bpmn-js は bundle が大きい）。 */
const BpmnViewer = lazy(() =>
  import("./bpmn-viewer").then((m) => ({ default: m.BpmnViewer })),
);

const NO_MARKS: DiagramMarks = { current: [], completed: [], error: [] };

export type ProcessOverviewProps = {
  /** 実行中の案件。渡すと実行状態（現在地・完了）をハイライトする。 */
  readonly caseRef?: CaseRef;
  /** 起票前など案件が無い場合のモデル id。図だけを出す。 */
  readonly processModelId?: string;
  /** 既定で開くか。 */
  readonly defaultOpen?: boolean;
};

/**
 * 作業中の画面に「プロセス全体」を添えるパネル。
 *
 * 起票・入力・承認の画面はフォームだけだと「この案件がどこまで進んで、次に誰へ
 * 回るのか」が分からない。図を同じ画面に置いて、判断に必要な文脈を与える。
 *
 * 案件があれば実行状態つき（詳細 API の XML ＋ task-info）、無ければモデルの図を
 * 出す。折りたためるのは、フォームが主役の画面で場所を取りすぎないため
 * （開閉は components/layout/collapsible-section.tsx）。
 */
export function ProcessOverview({
  caseRef,
  processModelId,
  defaultOpen = true,
}: ProcessOverviewProps) {
  const { t } = useTranslation("common");

  const instanceDiagram = useCaseDiagram(caseRef ?? null);
  const modelDiagram = useModelDiagram(
    caseRef === undefined ? (processModelId ?? null) : null,
  );

  const hasInstance = caseRef !== undefined;
  const xml = hasInstance
    ? (instanceDiagram.data?.xml ?? null)
    : (modelDiagram.data?.xml ?? null);
  const marks = hasInstance
    ? (instanceDiagram.data?.marks ?? NO_MARKS)
    : NO_MARKS;
  const isPending = hasInstance
    ? instanceDiagram.isPending
    : modelDiagram.isPending;
  const isError = hasInstance ? instanceDiagram.isError : modelDiagram.isError;

  return (
    <CollapsibleSection
      title={t("processOverview.title")}
      defaultOpen={defaultOpen}
    >
      {/* 実行状態があるときだけ色の意味を示す */}
      {hasInstance ? <Legend /> : null}

      {/* BpmnViewer の既定と同じ高さにする（違うと図が出た瞬間に跳ねる） */}
      {isPending ? <Skeleton className="h-72 w-full" /> : null}

      {isError || (!isPending && xml === null) ? (
        <p role="alert" className="text-muted-foreground text-sm">
          {t("processOverview.unavailable")}
        </p>
      ) : null}

      {xml === null ? null : (
        <Suspense fallback={<Skeleton className="h-72 w-full" />}>
          <BpmnViewer xml={xml} marks={marks} />
        </Suspense>
      )}
    </CollapsibleSection>
  );
}

function Legend() {
  const { t } = useTranslation("common");

  return (
    <ul className="text-muted-foreground flex flex-wrap gap-3 text-xs">
      <LegendItem
        className="bg-primary/20 border-primary"
        label={t("processOverview.legendCurrent")}
      />
      <LegendItem
        className="bg-muted border-border"
        label={t("processOverview.legendCompleted")}
      />
      <LegendItem
        className="bg-destructive/20 border-destructive"
        label={t("processOverview.legendError")}
      />
    </ul>
  );
}

function LegendItem({
  className,
  label,
}: {
  readonly className: string;
  readonly label: string;
}) {
  return (
    <li className="flex items-center gap-1">
      <span className={`inline-block size-3 rounded border ${className}`} />
      {label}
    </li>
  );
}
